/** Typed access to the versioned configuration. YAML is the editable source; the JSON beside it
 *  is generated by `pnpm oc:config` and is what the runtime imports. */
import scoring from "../generated/scoring_v1.json" with { type: "json" };
import qaGates from "../generated/qa_gates.json" with { type: "json" };
import llmPricing from "../generated/llm_pricing.json" with { type: "json" };
import enrichment from "../generated/enrichment.json" with { type: "json" };
import holidaysAu from "../generated/holidays_au.json" with { type: "json" };
import jurisdiction from "../generated/jurisdiction.json" with { type: "json" };
import webRegions from "../generated/web_regions.json" with { type: "json" };
import metrics from "../generated/metrics.json" with { type: "json" };
import { benchmarksFor, ownerManagerWage } from "./benchmarks.ts";
import email from "../generated/email.json" with { type: "json" };

export const scoringConfig = scoring as unknown as ScoringConfig;
export const SCORING_CONFIG_VERSION: string = scoring.version;

/** QA_GATES_AND_RELEASE §1 — the eleven checks, their thresholds and their fail actions. */
export const qaGatesConfig = qaGates as unknown as QaGatesConfig;
export const QA_GATES_VERSION: string = (qaGates as { version: string }).version;

export type GateId = "G1" | "G2" | "G3" | "G4" | "G5" | "G6" | "G7" | "G8" | "G9" | "G10" | "G11";
export type FailAction = "hold" | "shadow" | "deliver_flagged";

export interface QaGatesConfig {
  version: string;
  status: string;
  gates: Record<GateId, { name: string; fail_action: FailAction; [k: string]: unknown }>;
  released_sectors: Record<string, string[]>;
  hold_target_business_hours: number;
  critic_rubric: {
    criteria: string[];
    score_min: number;
    score_max: number;
    pass_mean: number;
    number_fix_stops_regeneration: boolean;
  };
  /** QA_GATES §5 — the post-delivery sample: the rate and the floor per week. */
  sampling: { rate: number; min_per_week: number };
  /** QA_GATES §3 — the five criteria a sector needs before it may be delivered without a human. */
  release_criteria: {
    blind_test: { min_businesses: number; min_hit_rate: number };
    shadow: { consecutive_clean_reports: number; min_mean_grade: number };
    calibration: { min_followups: number; max_abs_error_pct: number };
    counsel_signoff: { asserted_by_founder: boolean };
    refunds: { max_rate: number };
  };
  /** EVAL_BLIND_TEST — the two Sprint gates, and the holdout that may never be tuned on. */
  blind_test_gates: {
    S1: { businesses: number; min_hit_rate: number };
    S2: { businesses: number; min_hit_rate: number };
    holdout_of_first: { first: number; hold_out: number };
  };
}

/** DQ-10 model assignment, prices from the reference skill, and DQ-12's spend cap. */
export const llmPricingConfig = llmPricing as unknown as LlmPricingConfig;
export const LLM_PRICING_VERSION: string = (llmPricing as { version: string }).version;

export type ModelRole = "OC_MODEL_SCORING" | "OC_MODEL_CRITIC" | "OC_MODEL_FAST";

/**
 * Which transport carries a model's calls. `anthropic`, `workers-ai` and `google-ai-studio` are
 * implemented. Vertex and Bedrock remain named extension points. A model whose provider has no
 * transport fails loudly at dispatch, not at request time.
 */
export type ModelProvider = "anthropic" | "workers-ai" | "google-ai-studio" | "vertex" | "bedrock";

export interface ModelPrice {
  provider: ModelProvider;
  input_usd_per_mtok: number;
  output_usd_per_mtok: number;
  context: number;
}

export interface LlmPricingConfig {
  version: string;
  roles: Record<ModelRole, string>;
  models: Record<string, ModelPrice>;
  batch_discount: number;
  spend: { alert_usd_per_month: number; hard_cap_usd_per_month: number; over_cap_action: string };
  run_budgets_usd: { indication: number; deep: number; eval_run: number };
  /** The Workers AI daily allocation, recorded so it can be reported rather than routed around. */
  workers_ai_free_tier: {
    neurons_per_day: number;
    usd_per_1000_neurons: number;
    resets_at_utc: string;
    over_allocation_action: string;
  };
}

export type Band = "Exceptional" | "Strong" | "Interesting" | "Marginal" | "Pass";

export interface ScoringConfig {
  version: string;
  status: string;
  policy: {
    missing_value: "drop_and_reweight";
    missing_all_inputs_value: number;
    missing_all_inputs_flag: string;
    judgement_requires_reason: boolean;
    business_quality_cap_when_revenue_null: number;
  };
  bands: { exceptional: number; strong: number; interesting: number; marginal: number };
  blind_test_hit: { same_band: boolean; adjacent_band_within: number };
  business_quality: Record<string, any>;
  automation_potential: Record<string, any>;
  investment_score: Record<string, any>;
}

/** SCORING_SPEC §4 band boundaries. */
export function bandFor(score: number, bands = scoringConfig.bands): Band {
  if (score >= bands.exceptional) return "Exceptional";
  if (score >= bands.strong) return "Strong";
  if (score >= bands.interesting) return "Interesting";
  if (score >= bands.marginal) return "Marginal";
  return "Pass";
}

/** ARCHITECTURE §Enrichment, DQ-19, DQ-28 — the public-data collectors and what each may keep. */
export const enrichmentConfig = enrichment as unknown as EnrichmentConfig;
export const ENRICHMENT_VERSION: string = (enrichment as { version: string }).version;

export type CollectorName = "website" | "google_places" | "abn_lookup" | "job_ads";

export interface CollectorConfig {
  enabled: boolean;
  transport?: string;
  fills: string[];
  cacheable?: boolean;
  /** GA-46: the only fields this collector's answers may be kept beyond the run. */
  cacheable_fields?: string[];
  cache_ttl_hours?: number;
  timeout_ms?: number;
  attribution_required?: boolean;
  attribution?: string;
  verified?: boolean;
  unverified_note?: string;
  dropped_by?: string;
  reason?: string;
  [k: string]: unknown;
}

export interface EnrichmentConfig {
  version: string;
  status: string;
  cache_ttl_days: number;
  collectors: Record<CollectorName, CollectorConfig>;
}

/**
 * Australian public holidays and the IANA zone per state (DQ-41).
 *
 * Generated from `date-holidays` by `tools/index/gen-holidays.mjs`, not imported at runtime: a
 * Worker bundle carries a few kilobytes of dates rather than every country's rules. It bundles
 * rules and not a live feed, so state one-offs need checking against the gazette (GA-83).
 */
export const holidaysConfig = holidaysAu as unknown as HolidaysConfig;

export type AuState = "ACT" | "NSW" | "NT" | "QLD" | "SA" | "TAS" | "VIC" | "WA";

export interface HolidaysConfig {
  source: string;
  accessed: string;
  note: string;
  years: [number, number];
  zones: Record<AuState, string>;
  /** `{ "2026-01-26": "Australia Day" }` per state, public holidays only. */
  holidays: Record<AuState, Record<string, string>>;
}

/** DQ-18 — Resend, and the Spam Act rules every message obeys before it leaves. */
export const emailConfig = email as unknown as EmailConfig;
export const EMAIL_CONFIG_VERSION: string = (email as { version: string }).version;

export type MessageClass = "transactional" | "commercial";

export type ElectronicMessagesLawName = "SPAM_ACT" | "CASL" | "PECR";

/**
 * The electronic-message law of one market (GA-89).
 *
 * One set of rules per market rather than one for everybody, because wave 1 is `au`, `ca` and `uk`
 * and they differ where it counts: CASL has no business-to-business exemption, its implied consent
 * expires, and it wants sixty days of working unsubscribe rather than thirty.
 */
export interface ElectronicMessagesRules {
  law: ElectronicMessagesLawName;
  authority: string;
  identify_required: boolean;
  consent_required_for: MessageClass[];
  unsubscribe_required_for: MessageClass[];
  /** The kinds of consent this law recognises. Evidence naming any other kind is refused. */
  consent_kinds: string[];
  /** CASL's implied consent is time-limited; the Spam Act's inferred consent is not. */
  consent_may_expire: boolean;
  b2b_exempt: boolean;
  unsubscribe_valid_days: number;
  unsubscribe_honoured_within_business_days: number;
}

export interface EmailConfig {
  version: string;
  provider: string;
  endpoint: string;
  batch_endpoint: string;
  batch_max: number;
  idempotency_hours: number;
  timeout_ms: number;
  electronic_messages: Record<string, ElectronicMessagesRules>;
  list_unsubscribe_headers: boolean;
  residency: {
    provider_regions: string;
    client_data_allowed_in_body: boolean;
    attachments_require_acknowledgement: boolean;
  };
  classes: Record<MessageClass, { description: string; needs_consent: boolean; needs_unsubscribe: boolean }>;
}

export class NoElectronicMessagesRules extends Error {
  market: string;
  constructor(market: string, known: string[]) {
    super(`no electronic-message rules for ${market}; wave 1 is ${known.join(", ")} (ADR-018). `
      + "A market with no entry cannot be sent to — a default would apply the wrong country's law.");
    this.name = "NoElectronicMessagesRules";
    this.market = market;
  }
}

/** The rules for a market, or a refusal. Never a fallback: the fallback is another country's law. */
export function messagesLawFor(market: string): ElectronicMessagesRules {
  const rules = emailConfig.electronic_messages[market];
  if (rules === undefined) {
    throw new NoElectronicMessagesRules(market, Object.keys(emailConfig.electronic_messages));
  }
  return rules;
}

/**
 * ADR-016/017/018 — which layer of the Deal Discovery module may run where, how contact is made,
 * and which fee models are permitted. `packages/policy` reads this and nothing else, because
 * `JURISDICTION_STRATEGY §4` requires that no region-specific logic live in application code: a
 * legal development must be a config change and a ledger entry rather than a deploy.
 */
export const jurisdictionConfig = jurisdiction as unknown as JurisdictionConfig;
export const JURISDICTION_VERSION: string = (jurisdiction as { version: string }).version;

/**
 * The five market codes (ADR-014/018).
 *
 * Named `MarketCode` and not `Market` because `benchmarks.ts` already exports a `Market`, and that
 * one means something narrower and truer: the markets whose benchmark tables actually exist, which
 * is `au` alone today (GA-90). Two types with one name in one module is an ambiguity waiting to be
 * resolved the wrong way by whoever imports it next.
 */
export type MarketCode = "au" | "ca" | "uk" | "us" | "ie";
export type DataRegion = "au" | "us" | "uk" | "eu";
export type LayerName = "public_research" | "buyside_diligence" | "sellside_value_creation";
export type HardDenyId =
  | "NO_PRICE_RELAY" | "NO_CONTINGENT_FEE" | "NO_AGENCY_LANGUAGE" | "AI_DISCLOSURE" | "NO_MANDATE";
export type ElectronicMessagesLaw = "SPAM_ACT" | "CASL" | "PECR" | "CAN_SPAM";

export interface OutreachPolicy {
  /** ADR-017: outbound research calls, and only after the Constitution amendment is logged. */
  platform_identity_research_calls: boolean;
  /** An enquiry sent under a paying buyer's name, on that buyer's click. Needs Layer 2 open. */
  buyer_identity_enquiries: boolean;
  ai_disclosure_required: boolean;
  recording_notice_required: boolean;
  /** `"09:00-20:00"` in the callee's own local time, or false for a day with no calling at all. */
  calling_hours: Record<string, string | false>;
  dnc_list_provider: string;
  b2b_dnc_applies: boolean;
  electronic_messages_law: ElectronicMessagesLaw;
  unsubscribe_valid_days: number;
  /** CASL makes email the constrained channel in Canada and the Spam Act makes voice it in AU. */
  preferred_channel: "email" | "voice";
}

export interface ShareSaleHandling {
  permitted: boolean;
  /** Canada: blocked even after a provincial registration opens the property gate (C-08). */
  hard_block: boolean;
  escalation_required: boolean;
  escalation_note: string;
}

export interface ListingSource {
  platform: string;
  base_url?: string;
  enabled: boolean;
  /** C-09: a source is crawled only after somebody read its terms and dated the reading. */
  tos_reviewed_at?: string;
  tos_reviewed_by?: string;
  robots_respected?: boolean;
  notes?: string;
}

export interface JurisdictionPolicy {
  market: MarketCode;
  display_name: string;
  status: "live" | "layer1_only" | "off";
  layers: Record<LayerName, boolean>;
  layer_gate_reason: string | null;
  outreach: OutreachPolicy;
  fees: { permitted_models: string[] };
  registrations: { type: string; authority: string; required_before_trading: boolean; note?: string }[];
  share_sale_handling: ShareSaleHandling;
  copy_pack: string;
  prohibited_terms_pack: string;
  data_region: DataRegion;
}

/**
 * Whether any outbound call may be placed at all (ADR-017, Constitution Art. 9).
 *
 * Two ledger sequences rather than a boolean: a flag is something anyone can flip to make a test
 * pass, and a sequence is a claim about a ledger entry that `ledger.py verify` can check. Both must
 * be present — the founder's `AMENDMENT` and the Operating Partner's `CONCUR` — because Art. 9 says
 * an amendment is effective on the second of them, not the first.
 */
export interface OutboundCallsGate {
  adr: string;
  amendment_ledger_seq: number | null;
  concurred_ledger_seq: number | null;
  note: string;
}

export interface JurisdictionConfig {
  version: string;
  status: string;
  markets: MarketCode[];
  wave_1: MarketCode[];
  outbound_calls: OutboundCallsGate;
  hard_denies: { id: HardDenyId; reason: string }[];
  prohibited_fee_models_everywhere: string[];
  publishing: {
    human_review_required: boolean;
    right_of_reply_required_before_adverse: boolean;
    store_listing_copy: boolean;
    store_listing_images: boolean;
  };
  /** Keyed by market: a listing site is national, and its terms are a fact about the site. */
  listing_sources: Record<string, ListingSource[]>;
  jurisdictions: Record<string, JurisdictionPolicy>;
}

/** True only once both entries exist. Every voice action in every jurisdiction goes through this. */
export function outboundCallsPermitted(gate: OutboundCallsGate = jurisdictionConfig.outbound_calls): boolean {
  return typeof gate.amendment_ledger_seq === "number" && typeof gate.concurred_ledger_seq === "number";
}

/**
 * A jurisdiction to evaluate a **market-level** act under.
 *
 * Crawling a listing site is national: its terms of use are a fact about the site, not about a
 * state's agent-licensing regime. But the policy engine answers per jurisdiction, because that is
 * where the layer gate lives — so a market-level act is evaluated under the market's first
 * jurisdiction, which is safe precisely because Layer 1 is on in every one of them (the validator
 * refuses a jurisdiction with `public_research: false`). Passing a market code where a jurisdiction
 * is wanted throws instead, which is how this was found.
 */
export function jurisdictionForMarket(market: string): string {
  const found = JURISDICTION_CODES.find((code) => jurisdictionConfig.jurisdictions[code].market === market);
  if (found === undefined) {
    throw new Error(`no jurisdiction is configured for market ${market}; known markets: ${jurisdictionConfig.markets.join(", ")}`);
  }
  return found;
}

/** Every jurisdiction code the config knows, sorted, so a caller can enumerate without guessing. */
export const JURISDICTION_CODES: string[] = Object.keys(jurisdictionConfig.jurisdictions).sort();

/**
 * The policy for a jurisdiction code, or for a market when the licence is national. Throws rather
 * than falling back: a contact made under a jurisdiction we have no policy for is the failure this
 * whole file exists to prevent, and a default would hide it.
 */
export function jurisdictionPolicy(code: string): JurisdictionPolicy {
  const found = jurisdictionConfig.jurisdictions[code];
  if (found === undefined) {
    throw new Error(`no jurisdiction policy for ${code} — known: ${JURISDICTION_CODES.join(", ")}`);
  }
  return found;
}

export * from "./curves.ts";
export * from "./benchmarks.ts";

/**
 * Can the engine put a money range on a business in this sector, in this market?
 *
 * DQ-70: an absent owner-manager wage is the one missing benchmark that makes a run `not_priceable`
 * rather than merely thinly described — without it the bridge has no cap and an uncapped range on a
 * free document would be *less* conservative than the paid one. So the question is exactly "is
 * there a wage for this sector", asked in one place because the editorial queue and the engine must
 * not answer it differently.
 *
 * A market with no benchmark tables at all answers `false` (GA-90), which is the right answer: a
 * note there prints every section but the range.
 */
export function sectorPriceable(sector: string | null, market: string): boolean {
  if (sector === null || market !== "au") return false;
  return ownerManagerWage(benchmarksFor("au"), sector) !== null;
}

// ------------------------------------------------------------------ the public site's regions
/**
 * What the public site says that varies by market (`web_regions.yaml`, from the v0.1 design
 * delivery's copy packs). Which layers are on is `jurisdictionPolicy(jurisdictionForMarket(m)).layers`
 * — one source for a legal fact — and the fixed sentences are packages/copy's.
 *
 * A price carries `accepted`; an unaccepted one (the designer's proposals for `uk` and `ca`, DQ-90)
 * is printed as a placeholder, never as the number.
 */
export interface WebPrice { amount: number; accepted: boolean; source: string }
export interface WebRegion {
  name: string;
  currency: "AUD" | "GBP" | "CAD";
  audience: string;
  tax_label: string;
  tax_words: string;
  delivery: string;
  cutoff: string;
  contact_hours: string;
  mail: string;
  entity_line: string;
  spend_unit: string;
  broker_nav: string;
  broker_programme: boolean;
  intake: { cities: string[]; city_state: Record<string, string>; sectors: string[]; email_example: string; phone_example: string };
  engine_available: boolean;
  prices: Record<string, WebPrice>;
}
export interface WebRegionsConfig {
  version: string;
  live: MarketCode[];
  soon: { market: MarketCode; label: string }[];
  regions: Record<string, WebRegion>;
}
export const webRegionsConfig = webRegions as unknown as WebRegionsConfig;
export const WEB_REGIONS_VERSION: string = webRegionsConfig.version;
export const LIVE_MARKETS: MarketCode[] = webRegionsConfig.live;

export class NoWebRegion extends Error {
  market: string;
  constructor(market: string) {
    super(`no public site for market "${market}"; live markets are ${webRegionsConfig.live.join(", ")}`);
    this.market = market;
  }
}

export function webRegion(market: string): WebRegion {
  const r = webRegionsConfig.regions[market];
  if (r === undefined || !webRegionsConfig.live.includes(market as MarketCode)) throw new NoWebRegion(market);
  return r;
}

/** The accepted price, or null when the founder has not decided it — the caller prints a placeholder. */
export function acceptedPrice(market: string, key: string): number | null {
  const p = webRegion(market).prices[key];
  return p === undefined || !p.accepted ? null : p.amount;
}

// ------------------------------------------------------------------ the Friday snapshot's metrics
/** STATE §3 as data (`metrics.yaml`): the fourteen metrics, their targets and how each is measured. */
export interface MetricDef {
  id: string;
  label: string;
  target: string;
  target_value?: number;
  measured_by: "database" | "ledger";
  tables?: string[];
  note?: string;
}
export interface MetricsConfig { version: string; gate: string; gate_date: string; metrics: MetricDef[] }
export const metricsConfig = metrics as unknown as MetricsConfig;
export const METRICS_VERSION: string = metricsConfig.version;
